import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal, huidigeTaal } from '../i18n.js';
import Vlag from '../components/Vlag.jsx';
import { landnaam } from '../components/Wereldkaart.jsx';
import Lijngrafiek from '../components/Lijngrafiek.jsx';
import Stapelstaven from '../components/Stapelstaven.jsx';
import Balken from '../components/Balken.jsx';
import Periodes from '../components/Periodes.jsx';
import Lijsten from '../components/Lijsten.jsx';

/**
 * Firewall (docs/besluiten/meer-grafieken.md): wat de firewalls tegenhouden. Geweigerd verkeer in de tijd, van wie
 * (adres en land) en waarheen (poort), en de IPS: tegengehouden, doorgelaten en malware, met de regels die het vaakst
 * afgingen. Kleuren: geweigerd en tegengehouden in ADTS-cyaan, doorgelaten in hoog, malware in kritiek.
 */
const ips = () => [
  { sleutel: 'tegengehouden', label: t('tegengehouden'), kleur: 'var(--cyan-ink)' },
  { sleutel: 'doorgelaten', label: t('doorgelaten'), kleur: 'var(--st-serious)' },
  { sleutel: 'malware', label: t('malware'), kleur: 'var(--st-critical)' },
];

export default function Firewall({ klant }) {
  const [dagen, zetDagen] = useState(7);
  /* IPS-details: een soort (tegel) of een regel (balk) aangeklikt; nog een keer klikken sluit. */
  const [ipsKeuze, zetIpsKeuze] = useState(null);
  const kiesIps = (k) => zetIpsKeuze((oud) => (oud && oud.soort === k.soort && oud.regel === k.regel ? null : k));
  const ipsTegel = (soort, n, label, extra = '') => (
    <button type="button" className={`tile ${extra}`} aria-pressed={ipsKeuze?.soort === soort && !ipsKeuze?.regel}
      onClick={() => kiesIps({ soort, regel: null })} title={t('Klik voor de meldingen')}>
      <div className="n">{n}</div><div className="t">{label}</div></button>
  );
  const q = useQuery({ queryKey: ['grafiek', 'firewall', klant, dagen], queryFn: () => api.grafiek('firewall', klant, dagen), refetchInterval: 60_000 });
  const taal = huidigeTaal();
  const land = (cc) => (cc === 'intern' ? t('Intern') : cc === 'onbekend' ? t('Onbekend') : landnaam(cc, taal) ?? cc);
  const tegel = (n, label, extra = '') => <div className={`tile ${extra}`}><div className="n">{n}</div><div className="t">{label}</div></div>;
  const d = q.data?.data;
  const reeksen = ips();
  return (
    <>
      <div className="page-head"><div>
        <h1>{t('Firewall')}</h1>
        <p className="sub">{t('Wat de firewalls tegenhouden: geweigerd verkeer en de IPS.')}</p>
      </div></div>
      <div className="filters"><Periodes keuzes={[1, 7, 30]} waarde={dagen} onKies={zetDagen} /></div>
      {q.isPending ? <p className="muted">{t('Laden…')}</p> : q.isError ? <div className="melder rood">{t(q.error.message)}</div> : (
        <>
          <h2 className="sectiekop">{t('Geweigerd verkeer')}</h2>
          <div className="tiles">
            {tegel(getal(d.totals.geweigerd), t('geweigerde verbindingen'))}
            {tegel(getal(d.totals.adressen), t('adressen'))}
            {tegel(getal(d.totals.opLijst.adressen), t('op een threat-intel-lijst, samen {p}% van de pogingen', {
              p: d.totals.geweigerd ? Math.round((d.totals.opLijst.pogingen / d.totals.geweigerd) * 100) : 0 }))}
            {tegel(getal(d.poorten[0]?.n ?? 0), d.poorten[0] ? t('naar {poort}, de meest geprobeerde poort', { poort: `${d.poorten[0].proto}/${d.poorten[0].poort}` }) : t('geen poort'))}
          </div>
          <section className="card" style={{ marginBottom: 18 }}>
            <header><h2>{d.per === 'hour' ? t('Per uur') : t('Per dag')}</h2></header>
            <Lijngrafiek punten={d.geweigerd} perDag={d.per !== 'hour'} eenheid={t('geweigerd')} label={t('Geweigerd verkeer')} />
          </section>
          <div className="grid-2" style={{ marginBottom: 18 }}>
            <section className="card"><header><h2>{t('Adressen met de meeste pogingen')}</h2></header>
              {d.adressen.length ? (
                <table>
                  <thead><tr><th>{t('Adres')}</th><th>{t('Herkomst')}</th><th style={{ textAlign: 'right' }}>{t('Geblokkeerd')}</th>
                    <th style={{ textAlign: 'right' }}>{t('Toegestaan')}</th></tr></thead>
                  <tbody>{d.adressen.map((a) => (
                    <tr key={a.ip}>
                      <td className="mono">{a.ip}</td>
                      <td style={{ fontSize: 13 }}>{a.internal ? <span className="muted">{t('intern')}</span> : <><Vlag cc={a.cc} />{land(a.cc ?? 'onbekend')}</>}
                        {a.org && <><br /><span className="muted">{a.org}</span></>}<br /><Lijsten lijsten={a.lijsten} /></td>
                      <td className="num" style={{ textAlign: 'right' }}>{getal(a.n)}</td>
                      {/* Een adres van buiten dat ook doorgelaten werd: dat valt op. */}
                      <td className="num" style={{ textAlign: 'right' }}>{a.toegestaan
                        ? <span className={a.internal ? undefined : 'toe-ja'} title={t('verbindingen die de firewall van dit adres toeliet')}>{getal(a.toegestaan)}</span>
                        : <span className="muted">0</span>}</td>
                    </tr>
                  ))}</tbody>
                </table>
              ) : <p className="empty">{t('Niets geweigerd in deze periode.')}</p>}
            </section>
            <div className="stack">
              <section className="card"><header><h2>{t('Landen')}</h2><span className="meta">{t('naar pogingen')}</span></header>
                <Balken rijen={d.landen.map((l) => ({ naam: land(l.cc), voor: <Vlag cc={l.cc} />, n: l.n, extra: l.adressen === 1 ? t('1 adres') : t('{n} adressen', { n: getal(l.adressen) }) }))} /></section>
              <section className="card"><header><h2>{t('Doelpoorten')}</h2>
                <span className="meta legenda"><span><i style={{ background: 'var(--fw-blok)' }} />{t('geblokkeerd')}</span>
                  <span><i style={{ background: 'var(--fw-toe)' }} />{t('toegestaan')}</span></span></header>
                <Doelpoorten poorten={d.poorten} klant={klant} dagen={dagen} land={land} />
                {/* Toegestaan komt alleen binnen als de firewall het logt (docs/besluiten/threat-intel.md); anders lijkt 0 een uitkomst. */}
                {d.poorten.length > 0 && d.poorten.every((p) => !p.toegestaan) && (
                  <p className="noot">{t('Toegestaan is 0: de firewalls loggen toegestane verbindingen niet. Daarvoor moet op de toegangsregels "log at beginning of connection" aan staan.')}</p>
                )}</section>
            </div>
          </div>

          <h2 className="sectiekop">{t('IPS')}</h2>
          <div className="tiles">
            {ipsTegel('tegengehouden', getal(d.totals.tegengehouden), t('tegengehouden'))}
            {ipsTegel('doorgelaten', getal(d.totals.doorgelaten), t('doorgelaten'), d.totals.doorgelaten ? 'warn' : '')}
            {ipsTegel('malware', getal(d.totals.malware), t('malware'), d.totals.malware ? 'crit' : '')}
            {tegel(getal(d.ipsRegels.length), t('verschillende regels'))}
          </div>
          {!(d.totals.tegengehouden + d.totals.doorgelaten + d.totals.malware) ? (
            <section className="card"><p className="empty">{t('Geen IPS-meldingen in deze periode.')}</p></section>
          ) : (
            <>
              {ipsKeuze && <IpsMeldingen keuze={ipsKeuze} klant={klant} dagen={dagen} land={land} sluit={() => zetIpsKeuze(null)} />}
              <section className="card" style={{ marginBottom: 18 }}><header><h2>{{ hour: t('Per uur'), '6 hours': t('Per zes uur') }[d.perIps] ?? t('Per dag')}</h2>
                <span className="meta legenda">{reeksen.map((r) => <span key={r.sleutel}><i style={{ background: r.kleur }} />{r.label}</span>)}</span></header>
                <Stapelstaven punten={d.ips} reeksen={reeksen} perUur={d.perIps !== 'day'} label={t('IPS-meldingen')} /></section>
              <section className="card"><header><h2>{t('Regels die het vaakst afgingen')}</h2><span className="meta">{t('klik voor de meldingen')}</span></header>
                <Balken rijen={d.ipsRegels.map((r) => ({ naam: r.naam, n: r.n, extra: r.doorgelaten ? t('{n} doorgelaten', { n: getal(r.doorgelaten) }) : null }))}
                  onKies={(naam) => kiesIps({ soort: null, regel: naam })} gekozen={ipsKeuze?.regel ?? null} /></section>
            </>
          )}
          <p className="noot">{t('Threat intel: Spamhaus DROP, abuse.ch Feodo Tracker, blocklist.de en CINS Army, elke zes uur opgehaald. Een adres op een lijst is bekend; een adres op geen enkele lijst richt zich misschien gerichter op jullie.')}</p>
          <p className="noot">{t('Geweigerd: elke verbinding die een regel weigerde. IPS: Snort op de firewall; doorgelaten is een melding zonder blokkade (bijvoorbeeld een regel die alleen detecteert).')}</p>
        </>
      )}
    </>
  );
}

/** Een kort getal en een percentage, voor de tooltip. */
const deel = (n, van) => (van ? `${getal(n)} (${Math.round((n / van) * 100)}%)` : getal(n));

/**
 * Doelpoorten (Bernhard, 2026-10-26): per poort één balk in twee kleuren, geblokkeerd en toegestaan, als verhouding (elke
 * balk even lang: 443 met honderdduizenden toegestane verbindingen drukte de rest anders plat); de getallen staan ernaast.
 * Met de muis erover de getallen; klikken klapt de adressen open die er geblokkeerd en toegelaten werden. De volgorde
 * blijft naar geblokkeerd: dit is de lijst van wat aangevallen wordt.
 */
function Doelpoorten({ poorten, klant, dagen, land }) {
  const [open, zetOpen] = useState(null);
  const [hover, zetHover] = useState(null);
  if (!poorten.length) return <p className="empty">{t('Niets.')}</p>;
  const sleutel = (p) => `${p.proto}/${p.poort}`;
  return (
    <div className="poorten">
      {poorten.map((p) => {
        const k = sleutel(p);
        const samen = p.n + p.toegestaan;
        return (
          <div key={k} style={{ display: 'contents' }}>
            <button type="button" className="poort" aria-expanded={open === k} onClick={() => zetOpen(open === k ? null : k)}
              onMouseEnter={() => zetHover(k)} onMouseLeave={() => zetHover(null)} onFocus={() => zetHover(k)} onBlur={() => zetHover(null)}>
              <span className="naam">{k}</span>
              <span className="spoor">
                {p.n > 0 && <span className="blok" style={{ flex: p.n }} />}
                {p.toegestaan > 0 && <span className="toe" style={{ flex: p.toegestaan }} />}
              </span>
              <span className="num"><b>{getal(p.n)}</b>{' / '}{getal(p.toegestaan)}</span>
              {hover === k && (
                <span className="poort-tip" role="tooltip">
                  <b>{k}</b><br />
                  <i style={{ background: 'var(--fw-blok)' }} />{t('geblokkeerd')}: {deel(p.n, samen)}<br />
                  <i style={{ background: 'var(--fw-toe)' }} />{t('toegestaan')}: {deel(p.toegestaan, samen)}<br />
                  <span className="muted">{t('klik voor de adressen')}</span>
                </span>
              )}
            </button>
            {open === k && <PoortDetail p={p} klant={klant} dagen={dagen} land={land} />}
          </div>
        );
      })}
    </div>
  );
}

/** De adressen achter één poort: wie er geblokkeerd werd, en wie de firewall er toeliet. */
function PoortDetail({ p, klant, dagen, land }) {
  const q = useQuery({ queryKey: ['firewall-poort', klant, dagen, p.proto, p.poort],
    queryFn: () => api.firewallDetail('port', klant, dagen, { proto: p.proto, port: p.poort }) });
  const herkomst = (a) => (a.internal ? <span className="muted">{t('intern')}</span> : <><Vlag cc={a.cc} />{land(a.cc ?? 'onbekend')}</>);
  const tabel = (rijen, leeg) => (rijen.length ? (
    <table><tbody>{rijen.map((a) => (
      <tr key={a.ip}><td className="mono">{a.ip}</td><td>{herkomst(a)}{a.org && <span className="muted"> · {a.org}</span>}</td>
        <td className="num" style={{ textAlign: 'right' }}>{getal(a.n)}</td></tr>
    ))}</tbody></table>
  ) : <p className="muted" style={{ margin: 0 }}>{leeg}</p>);
  return (
    <div className="poort-detail">
      {q.isPending ? <span className="muted">{t('Laden…')}</span> : q.isError ? <span className="melder rood">{t(q.error.message)}</span> : (
        <div style={{ display: 'grid', gap: 12 }}>
          <div><h3><i style={{ background: 'var(--fw-blok)', display: 'inline-block', width: 9, height: 9, borderRadius: 2, marginRight: 5 }} />
            {t('Geblokkeerd, de meeste pogingen')}</h3>{tabel(q.data.data.geweigerd, t('Niets geblokkeerd.'))}</div>
          <div><h3><i style={{ background: 'var(--fw-toe)', display: 'inline-block', width: 9, height: 9, borderRadius: 2, marginRight: 5 }} />
            {t('Toegestaan, de meeste verbindingen')}</h3>{tabel(q.data.data.toegestaan, t('Niets toegestaan.'))}</div>
        </div>
      )}
    </div>
  );
}

const tijd = (d) => new Date(d).toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

/**
 * De IPS-meldingen zelf (Bernhard, 2026-10-26: "doorgelaten zijn er twee, welke zijn dat?"): de nieuwste honderd van de
 * aangeklikte soort of regel, met firewall, van en naar, de regel, wat Snort deed en onder welke toegangsregel. Bij het
 * bronadres wie het is ("wie heeft dat gedaan"): land, provider en de threat-intel-lijsten.
 */
function IpsMeldingen({ keuze, klant, dagen, land, sluit }) {
  const q = useQuery({ queryKey: ['firewall-ips', klant, dagen, keuze.soort, keuze.regel],
    queryFn: () => api.firewallDetail('ips', klant, dagen, { soort: keuze.soort, regel: keuze.regel }) });
  const kop = keuze.regel ?? { tegengehouden: t('Tegengehouden'), doorgelaten: t('Doorgelaten'), malware: t('Malware') }[keuze.soort];
  const d = q.data?.data;
  return (
    <section className="card" style={{ marginBottom: 18 }}>
      <header><h2>{kop}</h2>
        <span className="meta">{d && (d.totaal > d.meldingen.length ? t('de nieuwste {n} van {m}', { n: getal(d.meldingen.length), m: getal(d.totaal) })
          : t(d.totaal === 1 ? '1 melding' : '{n} meldingen', { n: getal(d.totaal) }))}{' · '}
          <button type="button" className="btn small" onClick={sluit}>{t('Sluiten')}</button></span></header>
      {q.isPending ? <p className="muted">{t('Laden…')}</p> : q.isError ? <div className="melder rood">{t(q.error.message)}</div> : !d.meldingen.length
        ? <p className="empty">{t('Geen meldingen in deze periode.')}</p> : (
          <div style={{ overflowX: 'auto' }}><table className="ips-tabel">
            <thead><tr><th>{t('Tijd')}</th><th>{t('Firewall')}</th><th>{t('Van')}</th><th>{t('Naar')}</th><th>{t('Regel')}</th><th>{t('Resultaat')}</th><th>{t('Toegangsregel')}</th></tr></thead>
            <tbody>{d.meldingen.map((m, i) => (
              <tr key={i}>
                <td className="mono" style={{ whiteSpace: 'nowrap' }}>{tijd(m.at)}</td>
                <td>{m.host}</td>
                {/* Wie: het adres met land, provider en de threat-intel-lijsten waar het op staat. */}
                <td><span className="mono">{m.src ?? '—'}</span>{m.src && <><br /><span style={{ fontSize: 12 }}>{m.internal ? <span className="muted">{t('intern')}</span>
                  : <><Vlag cc={m.cc} />{land(m.cc ?? 'onbekend')}</>}{m.org && <span className="muted"> · {m.org}</span>}</span>
                  {m.lijsten?.length > 0 && <><br /><Lijsten lijsten={m.lijsten} /></>}</>}</td>
                <td className="mono">{m.dst ? `${m.dst}${m.poort !== null && m.poort >= 0 ? `:${m.poort}` : ''}` : '—'}</td>
                <td className="regel">{m.naam ?? '—'}{(m.classificatie || m.sid) && <><br /><span className="muted">
                  {[m.classificatie, m.sid ? `SID ${m.sid}` : null, m.prioriteit ? t('prioriteit {n}', { n: m.prioriteit }) : null].filter(Boolean).join(' · ')}</span></>}</td>
                <td>{m.msgid === 430004 ? t('malware') : m.resultaat === 'Block' ? t('tegengehouden') : <span className="toe-ja">{m.resultaat ?? t('doorgelaten')}</span>}</td>
                <td>{m.toegangsregel ?? '—'}{m.beleid && <><br /><span className="muted">{m.beleid}</span></>}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
    </section>
  );
}
